/**
 * 拍摄计划剪贴板面板
 * 按每次拖入分组展示；组头可整组拖回日历，组内卡片可单条拖回
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import './ClipboardPanel.css';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { ReactComponent as LocationIcon } from '../../../../../assets/svg/Schedule/location.svg';
import { ReactComponent as DeleteIcon } from '../../../../../assets/svg/Schedule/delete.svg';
import { ReactComponent as AddToDateIcon } from '../../../../../assets/svg/Schedule/add.svg';
import { getSceneCodeCellBackgroundColor } from '../../../../Breakdown/ScenesTable/Utils/SceneCellUtils';
import type { ClipboardGroup, ClipboardScheduleItem } from '../../../Hooks/useScheduleClipboard';
import type { SceneInfo } from '../../../../../types/scene.types';
import { formatCalDateShiftDisplay } from '../../Calendar/hooks/useCalDateShiftEdit';
import '../../Calendar/Calendar.css';

const CLIPBOARD_DROPPABLE_ID = 'clipboard-slotList';

export const CLIPBOARD_DROPPABLE_ID_PREFIX = 'clipboard-slotList';

export function getClipboardDroppableId(scheduleID: string): string {
  return `${CLIPBOARD_DROPPABLE_ID_PREFIX}-${scheduleID}`;
}

interface ClipboardPanelProps {
  scheduleID: string | undefined;
  groups: ClipboardGroup[];
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
  isActiveTab: boolean;
  onDragOverClipboard?: (isOver: boolean) => void;
  /** 按索引删除某一剪贴组 */
  onRemoveGroup?: (groupIndex: number) => void;
  /** 将整组追加到日历当前选中日期 */
  onAddGroupToCurrentDate?: (groupIndex: number) => void | Promise<void>;
}

/** 组头：整组拖回日历 */
const DraggableClipboardDropGroupHeader: React.FC<{
  scheduleID: string;
  groupIndex: number;
  group: ClipboardGroup;
  onRemoveGroup?: (groupIndex: number) => void;
  onAddGroupToCurrentDate?: (groupIndex: number) => void | Promise<void>;
}> = ({ scheduleID, groupIndex, group, onRemoveGroup, onAddGroupToCurrentDate }) => {
  const { t } = useTranslation();
  const id = `clipboard-group-${scheduleID}-${group.id}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: {
      type: 'clipboardGroup',
      groupIndex,
      clipboardGroupItems: group.items,
      sourceCalDateShift: group.sourceCalDateShift,
    },
  });

  const dateLabel =
    (group.sourceDateStr || '').trim() || t('schedule.clipboardOriginalDateUnknown');
  const shiftRaw = group.sourceCalDateShift?.trim();
  const shiftDisplay = shiftRaw ? formatCalDateShiftDisplay(shiftRaw, t) : '';
  const dayNightSuffix =
    shiftDisplay !== ''
      ? t('schedule.clipboardGroupDayNightSuffix', { shift: shiftDisplay })
      : '';
  const title = t('schedule.clipboardGroupHeader', {
    n: groupIndex + 1,
    date: dateLabel,
    shiftSuffix: dayNightSuffix,
  });

  const addLabel = t('schedule.clipboardAddGroupToCurrentDate');

  return (
    <div className="clipboard-drop-group-header-wrap">
      {onAddGroupToCurrentDate ? (
        <button
          type="button"
          className="dayInfo-slot-card-delete-btn dayInfo-slot-card-delete-btn-in-card clipboard-drop-group-header-add-btn"
          title={addLabel}
          aria-label={addLabel}
          onClick={(e) => {
            e.stopPropagation();
            void onAddGroupToCurrentDate(groupIndex);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <AddToDateIcon className="dayInfo-slot-card-delete-icon" aria-hidden />
        </button>
      ) : null}
      <div
        ref={setNodeRef}
        className={`clipboard-drop-group-header ${isDragging ? 'clipboard-dragging' : ''}`}
        {...attributes}
        {...listeners}
        title={title}
      >
        <span className="clipboard-drop-group-header-text">{title}</span>
      </div>
      {onRemoveGroup ? (
        <button
          type="button"
          className="dayInfo-slot-card-delete-btn dayInfo-slot-card-delete-btn-in-card clipboard-drop-group-header-delete-btn"
          title={t('common.delete')}
          aria-label={t('common.delete')}
          onClick={(e) => {
            e.stopPropagation();
            onRemoveGroup(groupIndex);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DeleteIcon className="dayInfo-slot-card-delete-icon" aria-hidden />
        </button>
      ) : null}
    </div>
  );
};

/** 可拖拽的地点组卡片 */
const DraggableClipboardGroup: React.FC<{
  item: ClipboardScheduleItem;
  groupIndex: number;
  itemIndex: number;
  scheduleID: string;
  sourceCalDateShift?: string;
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
}> = ({ item, groupIndex, itemIndex, scheduleID, sourceCalDateShift, getSceneInfo }) => {
  const id = `clipboard-item-${scheduleID}-${groupIndex}-${itemIndex}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: {
      type: 'clipboard',
      sourceType: 'clipboard',
      clipboardItem: item,
      groupIndex,
      itemIndex,
      sourceCalDateShift,
    },
  });

  const location =
    item.type === 'SHOOTS'
      ? item.location ?? getSceneInfo(item.slots[0]?.slotBelongSceneID)?.sceneLocation ?? ''
      : '';

  return (
    <div
      ref={setNodeRef}
      className={`clipboard-slot-group ${isDragging ? 'clipboard-dragging' : ''}`}
    >
      <div className="clipboard-slot-group-header" {...attributes} {...listeners}>
        <LocationIcon className="clipboard-slot-group-header-icon" aria-hidden />
        <span className="clipboard-slot-group-header-text">{location || '-'}</span>
      </div>
      <div className="clipboard-slot-group-cards">
        {item.slots.map((pt, slotIndex) => (
          <DraggableClipboardSlotCard
            key={pt.slotID}
            slot={pt}
            item={item}
            groupIndex={groupIndex}
            itemIndex={itemIndex}
            slotIndex={slotIndex}
            scheduleID={scheduleID}
            sourceCalDateShift={sourceCalDateShift}
            getSceneInfo={getSceneInfo}
          />
        ))}
      </div>
    </div>
  );
};

/** 可拖拽的便签卡片 */
const DraggableClipboardNote: React.FC<{
  item: ClipboardScheduleItem;
  groupIndex: number;
  itemIndex: number;
  scheduleID: string;
  sourceCalDateShift?: string;
}> = ({ item, groupIndex, itemIndex, scheduleID, sourceCalDateShift }) => {
  const id = `clipboard-item-${scheduleID}-${groupIndex}-${itemIndex}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: {
      type: 'clipboard',
      sourceType: 'clipboard',
      clipboardItem: item,
      groupIndex,
      itemIndex,
      sourceCalDateShift,
    },
  });

  const content = item.slots[0]?.slotName ?? '';

  return (
    <div
      ref={setNodeRef}
      className={`clipboard-note-card ${isDragging ? 'clipboard-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <div className="clipboard-note-card-content">{content || '-'}</div>
    </div>
  );
};

/** 可单独拖回日历的场次卡片 */
const DraggableClipboardSlotCard: React.FC<{
  slot: ClipboardScheduleItem['slots'][number];
  item: ClipboardScheduleItem;
  groupIndex: number;
  itemIndex: number;
  slotIndex: number;
  scheduleID: string;
  sourceCalDateShift?: string;
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
}> = ({ slot, item, groupIndex, itemIndex, slotIndex, scheduleID, sourceCalDateShift, getSceneInfo }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `clipboard-slot-${scheduleID}-${groupIndex}-${itemIndex}-${slot.slotID}`,
    data: {
      type: 'clipboard',
      sourceType: 'clipboard',
      clipboardItem: { ...item, slots: [slot] },
      groupIndex,
      itemIndex,
      slotIndex,
      sourceCalDateShift,
    },
  });
  const scene = getSceneInfo(slot.slotBelongSceneID);
  const backgroundColor = getSceneCodeCellBackgroundColor(
    scene?.sceneInteriorExterior,
    scene?.sceneDayNight,
  );
  const code = scene?.sceneCode ?? '';
  const summary = scene?.scenePlotSummary ?? slot.slotName ?? '';

  return (
    <div
      ref={setNodeRef}
      className={`clipboard-slot-card ${isDragging ? 'clipboard-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <div
        className="clipboard-slot-card-code"
        style={{ '--dynamic-bg-color': backgroundColor } as React.CSSProperties}
      >
        {code}
      </div>
      <div className="clipboard-slot-card-summary">{summary}</div>
    </div>
  );
};

export const ClipboardPanel: React.FC<ClipboardPanelProps> = ({
  scheduleID,
  groups,
  getSceneInfo,
  isActiveTab,
  onDragOverClipboard,
  onRemoveGroup,
  onAddGroupToCurrentDate,
}) => {
  const { t } = useTranslation();
  const droppableId = scheduleID ? getClipboardDroppableId(scheduleID) : CLIPBOARD_DROPPABLE_ID;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: {
      type: 'clipboard',
      droppable: true,
    },
  });

  const hasAnyContent = groups.some((g) => g.items.length > 0);

  React.useEffect(() => {
    onDragOverClipboard?.(isOver);
  }, [isOver, onDragOverClipboard]);

  return (
    <div
      ref={setNodeRef}
      className={`clipboard-container ${isOver ? 'clipboard-slotList-drag-over' : ''} ${!isActiveTab ? 'clipboard-slotList-hidden' : ''}`}
    >
      {!isActiveTab ? (
        <div className="clipboard-slotList-dropzone" aria-hidden />
      ) : (
        <div className="clipboard-slotList">
          {!hasAnyContent ? (
            <div className="clipboard-slotList-empty">
              <span>{t('schedule.clipboardEmptyHint')}</span>
            </div>
          ) : (
            groups.map((group, groupIndex) =>
              group.items.length === 0 ? null : (
                <div key={group.id} className="clipboard-drop-group">
                  <DraggableClipboardDropGroupHeader
                    scheduleID={scheduleID ?? ''}
                    groupIndex={groupIndex}
                    group={group}
                    onRemoveGroup={onRemoveGroup}
                    onAddGroupToCurrentDate={onAddGroupToCurrentDate}
                  />
                  <div className="clipboard-drop-group-body">
                    {group.items.map((item, itemIndex) =>
                      item.type === 'NOTE' ? (
                        <DraggableClipboardNote
                          key={`note-${group.id}-${itemIndex}-${item.slots[0]?.slotID}`}
                          item={item}
                          groupIndex={groupIndex}
                          itemIndex={itemIndex}
                          scheduleID={scheduleID ?? ''}
                          sourceCalDateShift={group.sourceCalDateShift}
                        />
                      ) : (
                        <DraggableClipboardGroup
                          key={`slots-${group.id}-${itemIndex}-${item.location}-${item.shift}`}
                          item={item}
                          groupIndex={groupIndex}
                          itemIndex={itemIndex}
                          scheduleID={scheduleID ?? ''}
                          sourceCalDateShift={group.sourceCalDateShift}
                          getSceneInfo={getSceneInfo}
                        />
                      ),
                    )}
                  </div>
                </div>
              ),
            )
          )}
        </div>
      )}
    </div>
  );
};
